import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import TriangleAlert from '@mui/icons-material/ReportProblem';
import WarningIcon from '@mui/icons-material/Warning';
import { Alert, AlertTitle, Stack } from '@mui/material';
import React from 'react';
import Icon from '../Icon/Icon';
import { colorPalette } from '../Theme/colors';

export enum BannerColour {
  BLUE = 'blue',
  RED = 'red',
  SILVER = 'silver',
  ORANGE = 'orange',
  PURPLE = 'purple',
}

interface BannerProps {
  color: BannerColour;
  textColor?: 'primary' | 'black' | string;
  icon?:
    | 'check'
    | 'info'
    | 'error'
    | 'warning'
    | 'triangleAlert'
    | 'purpleInfo';
  iconColor?: string;
  title?: string;
  children?: React.ReactNode;
  sx?: object;
}

const bannerStyles: Record<string, { background: string }> = {
  blue: {
    background: colorPalette.blue.banner.main,
  },
  red: {
    background: colorPalette.red.light,
  },
  silver: {
    background: colorPalette.silver.main,
  },
  orange: {
    background: colorPalette.orange.light,
  },
  purple: {
    background: '#e5e7ef',
  },
};

const textColorMapping: Record<string, string> = {
  primary: colorPalette.primary.main,
  balck: colorPalette.common.black,
};

const Banner: React.FC<BannerProps> = ({
  color = BannerColour.BLUE,
  textColor = 'black',
  icon = 'info',
  iconColor = colorPalette.common.black,
  title,
  children,
  sx,
}) => {
  const stylesForColor = bannerStyles[color];
  const background = stylesForColor.background;
  const textColorValue =
    textColor in textColorMapping ? textColorMapping[textColor] : textColor;

  const iconMapping: Record<string, JSX.Element> = {
    check: <Icon name="checkCircle" size={22} />,
    info: <ErrorIcon sx={{ color: iconColor }} />,
    error: <ErrorIcon sx={{ color: iconColor }} />,
    purpleInfo: <InfoIcon sx={{ color: iconColor, fontSize: 19 }} />,
    warning: <WarningIcon sx={{ color: iconColor }} />,
    triangleAlert: <TriangleAlert sx={{ color: iconColor }} />,
  };

  const iconElement = iconMapping[icon];

  return (
    <Alert
      icon={iconElement}
      sx={{
        backgroundColor: background,
        color: textColorValue,
        '& .MuiAlertTitle-root': {
          color: textColorValue,
        },
        ...sx,
      }}
    >
      {title && <AlertTitle>{title}</AlertTitle>}
      <Stack spacing={2}>{children}</Stack>
    </Alert>
  );
};

export default Banner;
